import { intl } from "LocaleWrapper";
import { useApolloClient } from "@apollo/client";
import { Select } from "@cofano/cck";
import { SelectProps } from "@cofano/cck/Select/Select";
import { formatDate, FormattedDate } from "utils/DateUtils";
import { ChildPropsFromPagination } from "types/commons";
import { GetTemplateVersionsByNameAndLanguageCodeDocument, GetTemplateVersionsByNameAndLanguageCodeQuery } from "__generated__/view/templates/graphql/template.queries.generated";

type TemplateVersionSelectOption = ChildPropsFromPagination<GetTemplateVersionsByNameAndLanguageCodeQuery, "templateVersionsByNameAndLanguageCode">

const TemplateVersionSelect = ({ search, ...rest }: {
	search: {
		templateName: string;
		languageCode: string;
	};
} & Omit<SelectProps<TemplateVersionSelectOption, false>, "fetchOptions" | "paginated" | "multiple">) => {

	const client = useApolloClient();

	const onFetchOptions = async (_: string, page: number) => {
		try  {
			const result = await client.query({
				query: GetTemplateVersionsByNameAndLanguageCodeDocument,
				variables: {
					name: search.templateName,
					languageCode: search.languageCode,
					pagination: {
						page: page - 1,
						size: 5,
					},
				},
			});

			if (page === 1) {
				const content = result.data.templateVersionsByNameAndLanguageCode.content;
				return {
					options: [ {
						...content[0],
						id: 0,
						version: intl.formatMessage({ id: "CURRENT" }),
					}, ...content ],
					totalResults: result.data.templateVersionsByNameAndLanguageCode.totalElements + 1,
				};
			}

			return {
				options: result.data.templateVersionsByNameAndLanguageCode.content,
				totalResults: result.data.templateVersionsByNameAndLanguageCode.totalElements + 1,
			};
		} catch (response) {
			return {
				options: [],
				totalResults: 0,
			};
		}
	};

	return (
		<Select
			{...rest}
			getOptionLabel={option => option.version ? `${option.version} (${formatDate(option.created)})` : formatDate(option.created) ?? "-"}
			fetchOptions={onFetchOptions}
			renderOption={(props, option) => (
				<li {...props} key={option.id} style={{ display: "flex", justifyContent: "space-between" }}>
					<div>{option.version ?? "-"}</div>
					<small><i><FormattedDate date={option.created} /></i></small>
				</li>
			)}
			clearable={false}
			disableSearch
			paginated
		/>
	);
};

export default TemplateVersionSelect;
